// replace "_" → "-" and fall back to a neutral default *without* forcing "de-DE"
export function resolveLocale(raw, fallback = 'en') {
    const normalized = String(raw || '').replace('_', '-'); // "en_DE" → "en-DE"
    return Intl.NumberFormat.supportedLocalesOf([normalized])[0]
        || Intl.NumberFormat.supportedLocalesOf([navigator.language])[0]
        || fallback; // "en" is a safe final fallback
}

export function getActiveLocale() {
    return resolveLocale(window.tx_itemrepo?.currencyLocale || navigator.language || 'en');
}

export function parseNumberToLocalizedPrice(number) {
    const currency       = window.tx_itemrepo?.currency       || 'EUR'
    const currencyLocale = resolveLocale(window.tx_itemrepo?.currencyLocale, 'en');
    return new Intl.NumberFormat(currencyLocale, {
        style:   'currency',
        currency
    }).format(number)
}

/**
 * Extract just the localized currency symbol (e.g. “€” or “$”)
 */
export function getCurrencySymbol() {
    const currency       = window.tx_itemrepo?.currency       || 'EUR'
    const currencyLocale = resolveLocale(window.tx_itemrepo?.currencyLocale, 'en');
    const parts = new Intl.NumberFormat(currencyLocale, {
        style:   'currency',
        currency
    }).formatToParts(0)
    return parts.find(p => p.type === 'currency')?.value || ''
}

/**
 * If it’s a PRICE filter, return the currency symbol; otherwise return the unit.
 * Supports optional unitModifier (e.g. unit="cm", unitModifier="/m²" → "cm/m²").
 * For non-PRICE: returns a leading space + unit string (or empty string if none),
 * matching existing call sites that rely on this spacing.
 */
export function getUnitOrCurrency(meta) {
    if (!meta) return ''
    if (meta.type === 'PRICE') {
        return getCurrencySymbol()
    }

    const unit = meta.unit || ''
    const modifier = meta.unitModifier || ''
    const combined = `${modifier}${unit}` // no extra space between unit & modifier

    // keep leading space for non-PRICE to match previous behavior
    return combined ? ` ${combined}` : ''
}

export function formatLocalizedDecimal(n, { decimals = 2, locale = getActiveLocale() } = {}) {
    if (!Number.isFinite(n)) return '';
    return new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: true,
    }).format(n);
}

export function parseLocalizedDecimal(str, { locale = getActiveLocale() } = {}) {
    if (typeof str === 'number') return str;
    if (str == null) return NaN;
    const s = String(str).trim();
    if (!s) return NaN;

    const parts = new Intl.NumberFormat(locale).formatToParts(1000.5);
    const group   = parts.find(p => p.type === 'group')?.value || ',';
    const decimal = parts.find(p => p.type === 'decimal')?.value || '.';

    const normalized = s
        .replace(/\s+/g, '')
        .replaceAll(group, '')
        .replace(decimal, '.')
        .replace(/[^\d.-]/g, '');

    const num = Number(normalized);
    return Number.isFinite(num) ? num : NaN;
}

// pick separators by language, not region
export function getDecimalLocale() {
    const raw = window.tx_itemrepo?.currencyLocale || navigator.language || 'en';
    const normalized = String(raw).replace('_', '-'); // "en_DE" -> "en-DE"
    const lang = normalized.split('-')[0].toLowerCase(); // "en" | "de" | ...

    // enforce language defaults for separators
    if (lang === 'en') return 'en'; // dot decimals
    if (lang === 'de') return 'de-DE'; // comma decimals

    // fallback: try language-only, else neutral "en"
    return Intl.NumberFormat.supportedLocalesOf([lang])[0] || 'en';
}
